import React from 'react';
import { Check, Brain } from 'lucide-react';

interface CorrectionImpactDisplayProps {
  originalValue: string;
  correctedValue: string;
  originalConfidence?: number;
  entityLabel: string;
  correctionStats?: {
    total_corrections: number;
  };
  /** True when the correction was attached to a vendor pattern the system can reuse. */
  learningApplied?: boolean;
  onClose?: () => void;
}

/**
 * Confirms a correction was saved, and says what — if anything — the system did
 * with it.
 *
 * This panel used to claim two things that were not true. It showed "Template
 * updated / Template rules improved" whenever the document had been extracted
 * with a template, but EntityCorrectionWidget routes every correction to
 * /api/corrections, which only records a vendor pattern in the learning system —
 * no template row is ever touched. And it rendered `improvement_rate` as
 * "Accuracy improvement: +X%", where that value is
 * `len(new_vendors) / len(recent_vendors)`: a 0-1 ratio of how many recently
 * corrected vendors were new, which is neither accuracy nor a percentage.
 *
 * Both are gone. What remains is what the backend can actually vouch for.
 */
const CorrectionImpactDisplay: React.FC<CorrectionImpactDisplayProps> = ({
  originalValue,
  correctedValue,
  originalConfidence,
  entityLabel,
  correctionStats,
  learningApplied = false,
  onClose
}) => {
  return (
    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3 animate-in fade-in duration-300">
      {/* Success header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Check className="w-5 h-5 text-green-400" />
          <span className="text-sm font-medium text-green-300">
            Correction saved
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-green-400/60 hover:text-green-300 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>

      {/* Before/After comparison */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        {/* Before */}
        <div className="p-2 bg-red-500/10 rounded border border-red-500/20">
          <p className="text-red-400/70 mb-1 font-medium">Before:</p>
          <p className="text-red-200 break-all">{originalValue}</p>
          {originalConfidence !== undefined && (
            <p className="text-red-400/60 mt-1">
              Confidence: {Math.round(originalConfidence * 100)}%
            </p>
          )}
        </div>

        {/* After */}
        <div className="p-2 bg-green-500/10 rounded border border-green-500/20">
          <p className="text-green-400/70 mb-1 font-medium">After:</p>
          <p className="text-green-200 font-medium break-all">{correctedValue}</p>
        </div>
      </div>

      {/* Only shown when the backend reports the correction was attached to a
          vendor pattern — i.e. when there is genuinely something for a future
          document to match against. */}
      {learningApplied && (
        <div className="flex items-start gap-2 p-2 bg-coral-500/10 rounded border border-coral-500/30">
          <Brain className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" />
          <div className="text-xs">
            <p className="text-purple-700 dark:text-purple-300 font-medium mb-1">Saved for this supplier:</p>
            <ul className="text-stone-600 dark:text-foreground-secondary space-y-0.5">
              <li>• {entityLabel} correction recorded against this vendor</li>
              {correctionStats && (
                <li>• Total corrections recorded: {correctionStats.total_corrections}</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrectionImpactDisplay;
